import type { ErrorLike } from '@apollo/client';
import { GlobalStyles } from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import { TypeOptions } from 'react-toastify/dist/types';

import { ReturnComponent } from './utils/reactTypes';

// Overrides for react-toastify's look (previously toast-styles.css). Rendered alongside the
// toast container so it loads with it. Doubled class selectors (e.g. `.Toastify__toast.
// Toastify__toast`) are kept to preserve the original specificity over the library defaults.
const toastGlobalStyles = (
    <GlobalStyles
        styles={{
            // `:root:root` (specificity 0,2,0) so these variable overrides beat the library's
            //  own `:root` defaults regardless of stylesheet injection order.
            ':root:root': {
                '--toastify-color-info': '#2196f3',
                '--toastify-color-success': '#4caf50',
                '--toastify-color-warning': '#ff9800',
                '--toastify-color-error': '#d22f30',
                '--toastify-icon-color-info': '#fff',
                '--toastify-icon-color-success': '#fff',
                '--toastify-icon-color-warning': '#fff',
                '--toastify-icon-color-error': '#fff',
                '--toastify-toast-width': '20em',
                '--toastify-toast-min-height': '48px',
                '--toastify-toast-max-height': '800px',
                '--toastify-font-family': 'sans-serif',
                '--toastify-z-index': '1600',
                '--toastify-color-light': '#fafafa',
                '--toastify-color-dark': '#323232',
                '--toastify-color-transparent': 'rgba(255, 255, 255, 0.7)',
                '--toastify-color-progress-light': 'var(--toastify-color-light)',
                '--toastify-color-progress-dark': 'var(--toastify-color-dark)',
                '--toastify-color-progress-info': 'var(--toastify-color-info)',
                '--toastify-color-progress-success': 'var(--toastify-color-success)',
                '--toastify-color-progress-warning': 'var(--toastify-color-warning)',
                '--toastify-color-progress-error': 'var(--toastify-color-error)',
                '--toastify-text-color-light': '#757575',
                '--toastify-text-color-dark': '#fff',
                '--toastify-text-color-info': '#fff',
                '--toastify-text-color-success': '#fff',
                '--toastify-text-color-warning': '#fff',
                '--toastify-text-color-error': '#fff',
                '--toastify-spinner-color': '#616161',
                '--toastify-spinner-color-empty-area': '#e0e0e0',
            },
            '.Toastify__toast.Toastify__toast': {
                fontSize: '0.875rem',
                lineHeight: '1.25em',
                borderRadius: '8px',
                color: '#fff',
                boxShadow:
                    '0 3px 5px -1px rgb(0 0 0 / 20%), 0 6px 10px 0 rgb(0 0 0 / 14%), 0 1px 18px 0 rgb(0 0 0 / 12%)',
            },
            // Doubled selectors (specificity 0,2,0) so the per-type background beats the
            //  library's `.Toastify__toast-theme--light` background regardless of injection order.
            '.Toastify__toast--info.Toastify__toast--info': {
                background: 'var(--toastify-color-info)',
            },
            '.Toastify__toast--success.Toastify__toast--success': {
                background: 'var(--toastify-color-success)',
            },
            '.Toastify__toast--warning.Toastify__toast--warning': {
                background: 'var(--toastify-color-warning)',
            },
            '.Toastify__toast--error.Toastify__toast--error': {
                background: 'var(--toastify-color-error)',
            },
            '.Toastify__close-button.Toastify__close-button': { alignSelf: 'center' },
            '.newToast .Toastify__close-button': { alignSelf: 'flex-start', marginTop: '6px' },
            '.Toastify__toast.newToast-message.newToast-message': {
                border: '1px solid #757575',
                backgroundColor: '#26a69a',
                color: '#ffffff',
            },
            '.newToast-message .Toastify__close-button--light': {
                color: '#ffffff',
                opacity: 1,
            },
            '.Toastify__toast.newToast-alert.newToast-alert': {
                border: '1px solid #ec221f',
                backgroundColor: '#fee9e7',
                color: '#900b09',
            },
            '.newToast-alert .Toastify__close-button--light': {
                color: '#900b09',
                opacity: 1,
            },
        }}
    />
);

//////////////////////////////////////////////////////////////////////////////
// Internal helpers.
//////////////////////////////////////////////////////////////////////////////

// Q: How do we deduplicate toasts?
//
// A: We use the `toastId` option of react-toastify.
//
// react-toastify won’t display a toast if a toast with the same ID has already
//  being displayed (see https://fkhadra.github.io/react-toastify/use-a-custom-id).
//
// By making the message the toast ID, the same message will never be displayed
//  in multiple toasts at the same time.
//
// Toast with abandon, lads!
//
/*
 _..--""````""--.._
(_                _;..--""````""--.._
\ ```"""----"""```(_                _)
 '-.            .-\ ```"""----"""``` /
    `\        /`   '-.            .-'
      '-.__.-'        `\        /`
         ||             '-.__.-'
         ||                ||
         ||                ||
         ||                ||
    _..--||--.._           ||
   (_          _)     _..--||--.._
     ```""""```      (_          _)
                       ```""""```
*/

type AxleToastOptions = {
    deduplicate?: boolean;
    autoClose?: number | false | undefined;
};

export const genericToast = (
    message: string,
    type: TypeOptions,
    { deduplicate = true, autoClose }: AxleToastOptions = {},
) => {
    toast(message, {
        type,
        autoClose,
        ...(deduplicate && { toastId: [type, message].join(',') }),
    });
};

//////////////////////////////////////////////////////////////////////////////
// Public functions for toasting.
//////////////////////////////////////////////////////////////////////////////

export const successToast = (message: string, options: AxleToastOptions = {}) => {
    genericToast(message, toast.TYPE.SUCCESS, options);
};

export const warningToast = (message: string, options: AxleToastOptions = {}) => {
    genericToast(message, toast.TYPE.WARNING, options);
};

export const errorToast = (message: string, options: AxleToastOptions = {}) => {
    genericToast(message, toast.TYPE.ERROR, options);
};

export const onError = (error: ErrorLike) => {
    errorToast(error.message);
};

//////////////////////////////////////////////////////////////////////////////
// Public toast container.
//////////////////////////////////////////////////////////////////////////////

export const AxleToastContainer = (): ReturnComponent => (
    <>
        {toastGlobalStyles}
        <ToastContainer
            // Container options:
            limit={3} // Only show at most 3 toasts at a time. The rest will wait their turn.
            newestOnTop={false}
            rtl={false}
            // Defaults for toast options.
            position={toast.POSITION.BOTTOM_CENTER}
            autoClose={3000} // Stay on screen for 3 seconds.
            hideProgressBar // Otherwise, shows a countdown timer progress bar; kinda stressful and distracting.
            closeOnClick={true} // Allow clicking the toast to dismiss it.
            draggable // Allow dragging the toast to dismiss it.
            draggablePercent={60} // Make dragging to dismiss easier.
            draggableDirection={'y'} // Make it vertical dragging instead of horizontal.
            pauseOnFocusLoss={true} // Stop the countdown timer if the window loses focus.
            pauseOnHover={true} // Stop the countdown timer if the cursor is on the toast.
            // The rest of the styling is in `toastGlobalStyles` above.
            style={{ maxWidth: '80vw', width: '25rem' }}
        />
    </>
);
